import type { CountryCode } from 'libphonenumber-js';
import { AsYouType, getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';

/**
 * Default country for phone entry, overridable per deployment.
 *
 * `VITE_DEFAULT_PHONE_COUNTRY` is an ISO 3166-1 alpha-2 code, e.g. `US`, `GB`,
 * `GE`. Falls back to `US`. An unrecognised value falls back too rather than
 * throwing — a typo in an env file should not break the form.
 */
export const DEFAULT_COUNTRY: CountryCode = (() => {
  const configured = import.meta.env.VITE_DEFAULT_PHONE_COUNTRY as string | undefined;
  const normalized = configured?.trim().toUpperCase();
  const isSupported = (value: string | undefined): value is CountryCode =>
    !!value && (getCountries() as string[]).includes(value);

  return isSupported(normalized) ? normalized : 'US';
})();

/** `+1` for `US`. */
export const callingCodeOf = (country: CountryCode): string => `+${getCountryCallingCode(country)}`;

/**
 * The country a stored value belongs to, or the default when it can't be told.
 *
 * Existing records predate this input and hold free text like `(555) 123-4567`,
 * which carries no country. Those parse against the default rather than being
 * rejected, so an old record still opens with something sensible selected.
 */
export function countryOf(value: string, fallback: CountryCode = DEFAULT_COUNTRY): CountryCode {
  if (!value) return fallback;
  return parsePhoneNumberFromString(value, fallback)?.country ?? fallback;
}

/** Countries for the selector, ordered with the default first. */
export function countryOptions(): { code: CountryCode; flag: string; label: string }[] {
  const display = new Intl.DisplayNames(['en'], { type: 'region' });
  const options = getCountries().map((code) => ({
    code,
    flag: flagOf(code),
    label: `${display.of(code) ?? code} ${callingCodeOf(code)}`,
  }));

  return options.sort((a, b) => {
    if (a.code === DEFAULT_COUNTRY) return -1;
    if (b.code === DEFAULT_COUNTRY) return 1;
    return a.label.localeCompare(b.label);
  });
}

/**
 * Flag emoji for an ISO 3166-1 alpha-2 code.
 *
 * Built from regional indicator symbols rather than shipping image assets:
 * 'US' -> U+1F1FA U+1F1F8 -> 🇺🇸. Platforms that have no flag glyphs (notably
 * Windows) render the two letters instead, which still reads correctly beside
 * the calling code.
 */
export function flagOf(country: CountryCode): string {
  const REGIONAL_INDICATOR_A = 0x1f1e6;
  const LETTER_A = 'A'.charCodeAt(0);

  return country.replace(/./g, (letter) =>
    String.fromCodePoint(REGIONAL_INDICATOR_A + letter.charCodeAt(0) - LETTER_A),
  );
}

/**
 * Format for display: national style when the number is complete, otherwise
 * the partial text as typed so the caret does not jump while typing.
 */
export function formatForDisplay(value: string, country: CountryCode): string {
  if (!value) return '';

  const parsed = parsePhoneNumberFromString(value, country);
  if (parsed?.isPossible()) return parsed.formatNational();

  // AsYouType keeps partial input readable mid-entry.
  return new AsYouType(country).input(value);
}

/** Whether a non-empty value is structurally complete for its country. */
export function isCompleteNumber(value: string, country: CountryCode): boolean {
  if (!value.trim()) return true; // empty is not incomplete; these fields are optional
  return parsePhoneNumberFromString(value, country)?.isPossible() ?? false;
}

/**
 * Value to persist: E.164 when the number is structurally complete, otherwise
 * what the user typed.
 *
 * Deliberately non-lossy. Blocking submission on an unparseable number would
 * mean a patient whose phone predates this input could not be edited at all —
 * the field would reject a value the user never entered and may not be able to
 * correct. A partial number is stored as-is and can be fixed later.
 */
export function toStorageValue(value: string, country: CountryCode): string {
  const trimmed = value.trim();
  if (!trimmed) return '';

  const parsed = parsePhoneNumberFromString(trimmed, country);
  // isPossible, not isValid: isValid additionally requires the number to fall in
  // a range libphonenumber knows is assigned, which rejects both the reserved
  // 555 range used in examples and any genuinely new allocation. Refusing to
  // normalize a real number is worse than normalizing an unassigned one.
  return parsed?.isPossible() ? parsed.number : trimmed;
}
